import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { facebookAppId } from "~/config/socialMedia";

const FB_LOCALE = "sk_SK";
const FB_VERSION = "v20.0";
const POST_HEIGHT_INITIAL = 1200;
const POST_HEIGHT_FALLBACK = 750;
const POST_HEIGHT_MIN = 100;
const SDK_TIMEOUT_MS = 6000;

// ── SDK singleton ─────────────────────────────────────────────────────────────

type SDKState = "idle" | "loading" | "ready" | "error";
let sdkState: SDKState = "idle";
const sdkCallbacks: Array<(ok: boolean) => void> = [];

function loadFBSDK(): Promise<boolean> {
    return new Promise(resolve => {
        if (sdkState === "ready" || (window as any).FB) {
            sdkState = "ready";
            resolve(true);
            return;
        }
        if (sdkState === "error") { resolve(false); return; }

        sdkCallbacks.push(resolve);
        if (sdkState === "loading") return;
        sdkState = "loading";

        const timeout = setTimeout(() => {
            if (sdkState !== "ready") {
                sdkState = "error";
                sdkCallbacks.splice(0).forEach(cb => cb(false));
            }
        }, SDK_TIMEOUT_MS);

        const prev = (window as any).fbAsyncInit;
        (window as any).fbAsyncInit = function () {
            clearTimeout(timeout);
            (window as any).FB.init({ appId: facebookAppId, xfbml: false, version: FB_VERSION });
            sdkState = "ready";
            sdkCallbacks.splice(0).forEach(cb => cb(true));
            if (prev) prev();
        };

        if (!document.getElementById("facebook-jssdk")) {
            const s = document.createElement("script");
            s.id = "facebook-jssdk";
            s.src = `https://connect.facebook.net/${FB_LOCALE}/sdk.js`;
            s.async = true;
            s.defer = true;
            s.onerror = () => {
                clearTimeout(timeout);
                sdkState = "error";
                sdkCallbacks.splice(0).forEach(cb => cb(false));
            };
            document.body.appendChild(s);
        }
    });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function isFacebookVideoUrl(url: string): boolean {
    return /\/(videos|reel|watch)(\/|\?|$)/i.test(url) || /fb\.watch\//i.test(url);
}

function isFBOrigin(origin: string): boolean {
    try {
        const { hostname, protocol } = new URL(origin);
        return protocol === "https:" && (hostname === "facebook.com" || hostname.endsWith(".facebook.com"));
    } catch { return false; }
}

function parseResizeHeight(data: unknown): number | null {
    if (typeof data === "number" && data > POST_HEIGHT_MIN) return Math.round(data);
    if (typeof data === "string") {
        try { return parseResizeHeight(JSON.parse(data)); } catch {}
        const m = data.match(/\bheight["']?\s*[:=]\s*["']?(\d+)/i);
        if (m) { const n = parseInt(m[1], 10); if (n > POST_HEIGHT_MIN) return n; }
        return null;
    }
    if (Array.isArray(data)) {
        const nums = data.filter((v): v is number => typeof v === "number" && v > POST_HEIGHT_MIN && v < 15000);
        return nums.length > 0 ? Math.max(...nums) : null;
    }
    if (data && typeof data === "object") {
        const o = data as Record<string, unknown>;
        for (const key of ["height", "frameHeight", "frame_height", "content_height"]) {
            const v = o[key];
            if (typeof v === "number" && v > POST_HEIGHT_MIN) return Math.round(v);
            if (typeof v === "string") { const n = parseInt(v, 10); if (n > POST_HEIGHT_MIN) return n; }
        }
        for (const val of Object.values(o)) {
            if (val && typeof val === "object") {
                const inner = parseResizeHeight(val);
                if (inner) return inner;
            }
        }
    }
    return null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export type FacebookIframeEmbedProps = {
    url: string;
    width?: string | number;
    frame?: Window;
    [key: string]: unknown;
};

export function FacebookIframeEmbed({ url, width }: FacebookIframeEmbedProps): React.ReactElement {
    const containerRef = useRef<HTMLDivElement>(null);
    // Hidden off-screen XFBML element — exists only to establish the SDK's XD channel
    // so that postMessage resize events fire. The user never sees this element.
    const hiddenXFBMLRef = useRef<HTMLDivElement>(null);

    const [sdkReady, setSdkReady] = useState<boolean>(sdkState === "ready");
    const [pluginWidth, setPluginWidth] = useState(
        typeof width === "number" ? Math.min(750, Math.max(280, width)) : 500
    );
    const [iframeHeight, setIframeHeight] = useState(POST_HEIGHT_INITIAL);
    const heightRef = useRef(POST_HEIGHT_INITIAL);
    const firstResizeRef = useRef(false);

    const isVideo = isFacebookVideoUrl(url);
    const videoHeight = Math.round((pluginWidth * 9) / 16) + 72;

    // Kick off SDK load
    useEffect(() => {
        if (typeof window === "undefined" || sdkReady) return;
        loadFBSDK().then(ok => { if (ok) setSdkReady(true); });
    }, []);

    // Parse XFBML in the hidden container to activate the SDK's XD channel.
    // This makes the SDK relay resize postMessages even though the visible embed
    // is a plain post.php iframe (which keeps native target="_blank" link behaviour).
    useEffect(() => {
        if (!sdkReady || isVideo || !hiddenXFBMLRef.current || typeof window === "undefined") return;
        (window as any).FB.XFBML.parse(hiddenXFBMLRef.current);
    }, [sdkReady, url, isVideo]);

    // Measure container width for responsive sizing
    useLayoutEffect(() => {
        if (!containerRef.current) return;
        const el = containerRef.current;
        const measure = () => {
            const w = Math.floor(el.getBoundingClientRect().width);
            if (w > 0) setPluginWidth(Math.min(750, Math.max(280, w)));
        };
        measure();
        if (typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, [url]);

    // Reset height when post URL changes
    useLayoutEffect(() => {
        firstResizeRef.current = false;
        heightRef.current = POST_HEIGHT_INITIAL;
        setIframeHeight(POST_HEIGHT_INITIAL);
    }, [url]);

    // Resize events relayed by the SDK's XD channel from the hidden XFBML element
    useEffect(() => {
        if (isVideo || typeof window === "undefined") return;
        const onMessage = (e: MessageEvent) => {
            if (!isFBOrigin(e.origin)) return;
            const h = parseResizeHeight(e.data);
            if (!h) return;
            if (!firstResizeRef.current || h > heightRef.current) {
                firstResizeRef.current = true;
                heightRef.current = h;
                setIframeHeight(h);
            }
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [isVideo, url]);

    // Fallback height for when the SDK is blocked (no XD channel, no resize messages)
    const handleIframeLoad = () => {
        if (isVideo || firstResizeRef.current || sdkReady) return;
        heightRef.current = POST_HEIGHT_FALLBACK;
        setIframeHeight(POST_HEIGHT_FALLBACK);
    };

    const postIframeSrc = isVideo
        ? `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&width=${pluginWidth}&height=${videoHeight}&show_text=false&autoplay=false&appId=${facebookAppId}&locale=${FB_LOCALE}`
        : `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(url)}&width=${pluginWidth}&height=${POST_HEIGHT_INITIAL}&show_text=true&appId=${facebookAppId}&locale=${FB_LOCALE}`;

    return (
        <div ref={containerRef} style={{ width: "100%" }}>
            {/* Hidden XFBML — off-screen, never visible, only activates XD channel */}
            {sdkReady && !isVideo && (
                <div
                    ref={hiddenXFBMLRef}
                    aria-hidden="true"
                    style={{
                        position: "fixed",
                        top: -9999,
                        left: 0,
                        width: pluginWidth,
                        opacity: 0,
                        pointerEvents: "none",
                        overflow: "hidden",
                    }}
                >
                    <div
                        className="fb-post"
                        data-href={url}
                        data-width={pluginWidth}
                        data-show-text="true"
                    />
                </div>
            )}
            {/* Visible plain iframe — Facebook sets links to target="_blank" here */}
            <iframe
                key={postIframeSrc}
                title={isVideo ? "Facebook video" : "Facebook príspevok"}
                src={postIframeSrc}
                width={pluginWidth}
                height={isVideo ? videoHeight : iframeHeight}
                style={{
                    border: "none",
                    width: "100%",
                    maxWidth: "100%",
                    display: "block",
                    overflow: "auto",
                }}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                onLoad={handleIframeLoad}
            />
        </div>
    );
}
